(() => {
  if (window.AHCPdpCoreLoaded) return;
  window.AHCPdpCoreLoaded = true;
  const initialized = new WeakSet();
  const reduceMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const rootRoute = () => (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  const cartAddRoute = () => (window.routes && window.routes.cart_add_url) || `${rootRoute()}cart/add`;
  const cartRoute = () => (window.routes && window.routes.cart_url) || `${rootRoute()}cart`;

  const parseJSON = (node, fallback = []) => {
    if (!node) return fallback;
    try { return JSON.parse(node.textContent); } catch (_error) { return fallback; }
  };

  const formatMoney = (cents, currency) => {
    const value = Number(cents || 0) / 100;
    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'en-US', {
        style: 'currency',
        currency: currency || 'USD'
      }).format(value);
    } catch (_error) {
      return `$${value.toFixed(2)}`;
    }
  };

  const showToast = (root, message) => {
    const toast = root.querySelector('[data-ahc-toast]') || document.querySelector('[data-ahc-global-toast]');
    if (!toast) return;
    toast.textContent = message;
    toast.classList.add('is-visible');
    window.clearTimeout(toast._ahcTimer);
    toast._ahcTimer = window.setTimeout(() => toast.classList.remove('is-visible'), 3300);
  };

  const sectionHTML = (html, selector) => {
    if (!html) return null;
    const documentFragment = new DOMParser().parseFromString(html, 'text/html');
    return documentFragment.querySelector(selector);
  };

  const nextPaint = () => new Promise((resolve) => {
    window.requestAnimationFrame(() => window.requestAnimationFrame(resolve));
  });

  const updateCartIconBubble = (html) => {
    if (!html) return false;
    const source =
      sectionHTML(html, '#shopify-section-cart-icon-bubble') ||
      sectionHTML(html, '.shopify-section');
    const target = document.getElementById('cart-icon-bubble');
    if (!source || !target) return false;
    target.innerHTML = source.innerHTML;
    return true;
  };

  const drawerMarkupLooksUsable = (drawer) => {
    if (!drawer) return false;
    const inner = drawer.querySelector('.drawer__inner');
    const header = drawer.querySelector('.drawer__header');
    const cartItems = drawer.querySelector('#CartDrawer-CartItems');
    return Boolean(inner && header && cartItems);
  };

  const mountCartDrawer = async (html, trigger) => {
    if (!html) return false;

    const nextSection = sectionHTML(html, '#shopify-section-cart-drawer');
    const nextDrawer = sectionHTML(html, 'cart-drawer');

    if (!nextDrawer || !drawerMarkupLooksUsable(nextDrawer)) return false;

    const currentSection = document.getElementById('shopify-section-cart-drawer');
    const currentDrawer = document.querySelector('cart-drawer');

    if (currentSection && nextSection) {
      currentSection.replaceWith(nextSection);
    } else if (currentDrawer) {
      currentDrawer.replaceWith(nextDrawer);
    } else {
      return false;
    }

    await nextPaint();

    const activeDrawer = document.querySelector('cart-drawer');
    if (!activeDrawer || !drawerMarkupLooksUsable(activeDrawer)) return false;

    if (typeof activeDrawer.setActiveElement === 'function') {
      activeDrawer.setActiveElement(trigger);
    }

    if (typeof activeDrawer.open !== 'function') return false;

    activeDrawer.open(trigger);
    await nextPaint();

    return drawerMarkupLooksUsable(activeDrawer);
  };

  const fetchFreshCartSections = async () => {
    const base = rootRoute();
    const response = await fetch(
      `${base}?sections=cart-drawer,cart-icon-bubble&_=${Date.now()}`,
      {
        method: 'GET',
        headers: {
          Accept: 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        },
        credentials: 'same-origin',
        cache: 'no-store'
      }
    );

    if (!response.ok) {
      throw new Error(`Cart section refresh failed (${response.status})`);
    }

    const sections = await response.json();
    return sections && typeof sections === 'object' ? sections : {};
  };

  const renderCartDrawerReliable = async (state, trigger) => {
    let sections = state && state.sections ? state.sections : {};

    if (sections['cart-icon-bubble']) {
      updateCartIconBubble(sections['cart-icon-bubble']);
    }

    let rendered = await mountCartDrawer(sections['cart-drawer'], trigger);
    if (rendered) return true;

    try {
      sections = await fetchFreshCartSections();

      if (sections['cart-icon-bubble']) {
        updateCartIconBubble(sections['cart-icon-bubble']);
      }

      rendered = await mountCartDrawer(sections['cart-drawer'], trigger);
      if (rendered) return true;
    } catch (error) {
      console.warn('[AHC cart] Fresh cart drawer render failed:', error);
    }

    return false;
  };

  const submitProductForm = async (form, root, trigger) => {
    if (root.dataset.ahcSubmitting === 'true') return;
    root.dataset.ahcSubmitting = 'true';
    const errorNode = root.querySelector('[data-ahc-form-error]');
    const button = trigger || form.querySelector('[type="submit"]');
    const previousMarkup = button ? button.innerHTML : '';
    root.querySelectorAll('[data-ahc-atc]').forEach((node) => { node.disabled = true; });
    if (errorNode) errorNode.classList.remove('is-visible');
    if (button) {
      button.disabled = true;
      button.setAttribute('aria-busy', 'true');
      button.textContent = root.dataset.addingLabel || 'ADDING TO CART…';
    }

    try {
      const formData = new FormData(form);
      root._ahcNormalizePlan?.(formData);
      formData.set('sections', 'cart-drawer,cart-icon-bubble');
      formData.set('sections_url', window.location.pathname);
      const route = cartAddRoute();
      const addUrl = route.endsWith('.js') ? route : `${route}.js`;
      const response = await fetch(addUrl, {
        method: 'POST',
        headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body: formData
      });
      const state = await response.json();
      if (!response.ok || state.status) throw new Error(state.description || state.message || 'This selection could not be added.');

      if (root.dataset.skipCart === 'true') {
        window.location.assign(root.dataset.cartPageUrl || cartRoute());
        return;
      }

      const drawerRendered = await renderCartDrawerReliable(state, button);

      document.dispatchEvent(new CustomEvent('cart:updated', {
        bubbles: true,
        detail: { state, drawerRendered }
      }));

      if (drawerRendered) {
        showToast(root, root.dataset.addedLabel || 'Added to cart.');
      } else {
        showToast(root, 'Added to cart. Opening cart…');
        window.setTimeout(() => {
          window.location.assign(cartRoute());
        }, 120);
      }
    } catch (error) {
      const message = error && error.message ? error.message : 'Please try again.';
      if (errorNode) {
        errorNode.textContent = message;
        errorNode.classList.add('is-visible');
        errorNode.focus({ preventScroll: true });
      } else {
        window.alert(message);
      }
    } finally {
      root.dataset.ahcSubmitting = 'false';
      if (button) {
        button.disabled = false;
        button.removeAttribute('aria-busy');
        button.innerHTML = previousMarkup;
      }
      root._ahcSync?.();
    }
  };

  const initCountdown = (node) => {
    if (initialized.has(node)) return;
    initialized.add(node);
    const minutes = Math.max(1, Number(node.dataset.minutes || 30));
    const duration = minutes * 60 * 1000;
    const storageKey = `ahc_pdp_timer_${node.dataset.timerKey || 'global'}`;
    let deadline = 0;
    try { deadline = Number(localStorage.getItem(storageKey) || 0); } catch (_error) { deadline = 0; }
    if (!deadline || deadline <= Date.now()) {
      deadline = Date.now() + duration;
      try { localStorage.setItem(storageKey, String(deadline)); } catch (_error) { /* no-op */ }
    }
    const hours = node.querySelector('[data-hours]');
    const mins = node.querySelector('[data-minutes]');
    const seconds = node.querySelector('[data-seconds]');
    const render = () => {
      let difference = deadline - Date.now();
      if (difference <= 0) {
        deadline = Date.now() + duration;
        difference = duration;
        try { localStorage.setItem(storageKey, String(deadline)); } catch (_error) { /* no-op */ }
      }
      const totalSeconds = Math.floor(difference / 1000);
      const pad = (value) => String(value).padStart(2, '0');
      if (hours) hours.textContent = pad(Math.floor(totalSeconds / 3600));
      if (mins) mins.textContent = pad(Math.floor((totalSeconds % 3600) / 60));
      if (seconds) seconds.textContent = pad(totalSeconds % 60);
    };
    render();
    node._ahcInterval = window.setInterval(render, 1000);
  };

  const initProductRoot = (root) => {
    if (initialized.has(root)) return;
    initialized.add(root);
    const variants = parseJSON(root.querySelector('[data-ahc-variants]'));
    const currency = root.dataset.currency || 'USD';
    const variantSelect = root.querySelector('[data-ahc-variant]');
    const quantityInput = root.querySelector('[data-ahc-quantity]');
    const packButtons = [...root.querySelectorAll('[data-ahc-pack]')];
    const variantPacks = root.dataset.variantPacks === 'true';
    const packVariant = (quantity) => variants.find((variant) => variant.title === `${quantity} ${Number(quantity) === 1 ? 'Bottle' : 'Bottles'}`);
    const cartQuantity = () => variantPacks ? 1 : activeQuantity;
    const forms = [...root.querySelectorAll('form.ahc-fem-native-form')];
    const form = forms[0];
    const nativePlan = root.querySelector('[data-ahc-plan]');
    const nativePlanField = root.querySelector('[data-ahc-native-plan]');
    const widgetSelector = 'kaching-subscriptions';
    const getPlanState = () => {
      const variant = getVariant();
      const allocations = variant?.selling_plan_allocations || [];
      const widget = form?.querySelector(widgetSelector);
      const appFields = [...(form?.elements || [])].filter((node) =>
        node.name === 'selling_plan' && node !== nativePlan && !node.disabled &&
        (!['radio', 'checkbox'].includes(node.type) || node.checked));
      const appReady = Boolean(widget && (widget.querySelector('kaching-subscriptions-block, kaching-subscriptions-block-v2')?.childElementCount ||
        widget.shadowRoot?.childElementCount || (widget.childElementCount && appFields.length)));
      if (nativePlanField) nativePlanField.hidden = appReady;
      if (nativePlan) {
        nativePlan.disabled = appReady;
        [...nativePlan.options].forEach((option) => {
          const allowed = option.value ? allocations.some((allocation) => String(allocation.selling_plan_id) === option.value) : !variant?.requires_selling_plan;
          option.disabled = !allowed;
          option.hidden = !allowed;
        });
        if (!appReady && (!nativePlan.selectedOptions.length || nativePlan.selectedOptions[0].disabled)) {
          nativePlan.value = [...nativePlan.options].find((option) => !option.disabled)?.value || '';
        }
      }
      const store = widget?.querySelector('kaching-subscriptions-block-v2')?.widgetStore;
      const id = appReady && variantPacks && store
        ? String(store.selectedSellingPlanId.value || '')
        : appReady ? (appFields[appFields.length - 1]?.value || '') : (nativePlan?.value || '');
      if (appReady && variantPacks && store) {
        appFields.forEach((input) => { if (input.type === 'hidden') input.value = id; });
      }
      if (!appReady && nativePlan && form) {
        let input = form.querySelector('[data-ahc-plan-input]');
        if (!input && !nativePlan.name) {
          input = document.createElement('input');
          input.type = 'hidden';
          input.name = 'selling_plan';
          input.setAttribute('data-ahc-plan-input', '');
          form.appendChild(input);
        }
        if (input && input.value !== id) input.value = id;
      }
      const allocation = allocations.find((item) => String(item.selling_plan_id) === String(id));
      return { id, allocation, valid: id ? Boolean(allocation) : !variant?.requires_selling_plan, appReady };
    };
    const normalizePlan = (data) => {
      const { id } = getPlanState();
      data.delete('selling_plan');
      if (id) data.set('selling_plan', id);
    };
    root._ahcNormalizePlan = normalizePlan;
    const galleryTrack = root.querySelector('[data-ahc-gallery-track]');
    const gallerySlides = [...root.querySelectorAll('[data-ahc-gallery-slide]')];
    const galleryThumbs = [...root.querySelectorAll('[data-ahc-gallery-thumb]')];
    let activeQuantity = Number((packButtons.find((item) => item.getAttribute('aria-pressed') === 'true') || packButtons[0])?.dataset.qty || quantityInput?.value || 1);
    const readQuantity = () => {
      if (variantPacks) return;
      const quantity = Number(quantityInput?.value);
      if (!Number.isInteger(quantity) || quantity < 1 || quantity === activeQuantity) return;
      activeQuantity = quantity;
      packButtons.forEach((pack) => pack.setAttribute('aria-pressed', Number(pack.dataset.qty) === activeQuantity ? 'true' : 'false'));
    };

    const getVariant = () => {
      if (variantPacks) return packVariant(activeQuantity) || variants[0] || null;
      const id = variantSelect ? variantSelect.value : forms[0]?.querySelector('[name="id"]')?.value;
      return variants.find((variant) => String(variant.id) === String(id)) || variants[0] || null;
    };

    const subscriptionBlockSelector = 'kaching-subscriptions-block, kaching-subscriptions-block-v2';
    const syncSubscriptionPricing = () => {
      if (!packButtons.length) return;
      const variant = getVariant();
      form?.querySelectorAll(subscriptionBlockSelector).forEach((widget) => {
        // AHC packs use native Shopify variants/quantities, not a Bundles offer.
        // Stale bundle totals override the subscription widget's quantity pricing.
        if (widget.hasAttribute('bundle-pricing-data')) widget.removeAttribute('bundle-pricing-data');
        if (variant && widget.getAttribute('variant') !== String(variant.id)) {
          const store = widget.widgetStore;
          const kind = store?.selectedKind.value;
          const frequency = store?.viewModel.value.options.flatMap((option) => option.frequencies || [])
            .find((item) => String(item.sellingPlanId) === String(store?.selectedSellingPlanId.value));
          widget.setAttribute('variant', String(variant.id));
          if (variantPacks && store) {
            store.setVariant(String(variant.id));
            const nextFrequency = store.viewModel.value.options.flatMap((option) => option.frequencies || [])
              .find((item) => item.deliveryInterval?.interval === frequency?.deliveryInterval?.interval &&
                item.deliveryInterval?.frequency === frequency?.deliveryInterval?.frequency);
            if (kind) store.selectKind(kind);
            if (kind === 'subscription' && nextFrequency) store.selectFrequency(nextFrequency.sellingPlanId);
          }
        }
        if (widget.getAttribute('quantity') !== String(cartQuantity())) {
          widget.setAttribute('quantity', String(cartQuantity()));
        }
      });
    };

    const canBuyQuantity = (variant, quantity) => {
      if (!variant || !variant.available) return false;
      const rule = variant.quantity_rule || {};
      const requested = Number(quantity || 1);
      const minimum = Math.max(1, Number(rule.min || 1));
      const increment = Math.max(1, Number(rule.increment || 1));
      const maximum = Number(rule.max || 0);
      if (requested < minimum || (maximum > 0 && requested > maximum)) return false;
      if ((requested - minimum) % increment !== 0) return false;
      if (variant.inventory_management !== 'shopify' || variant.inventory_policy === 'continue') return true;
      return Number(variant.inventory_quantity || 0) >= requested;
    };

    const syncGalleryToVariant = (variant) => {
      if (!variant || !variant.featured_media || !galleryTrack) return;
      const index = gallerySlides.findIndex((slide) => String(slide.dataset.mediaId) === String(variant.featured_media.id));
      if (index < 0) return;
      galleryTrack.scrollTo({ left: galleryTrack.clientWidth * index, behavior: reduceMotion() ? 'auto' : 'smooth' });
    };

    const sync = (options = {}) => {
      const variant = getVariant();
      if (!variant) return;
      if (variantSelect && variantPacks) variantSelect.value = String(variant.id);
      activeQuantity = Math.max(1, Number(activeQuantity || 1));
      forms.forEach((form) => {
        const idInput = form.querySelector('[name="id"]');
        const qtyInput = form.querySelector('[name="quantity"]');
        if (idInput) idInput.value = variant.id;
        if (qtyInput) qtyInput.value = cartQuantity();
      });
      if (quantityInput) quantityInput.value = cartQuantity();
      syncSubscriptionPricing();
      const plan = getPlanState();
      const unitPrice = Number(plan.allocation?.price ?? variant.price);
      const comparePrice = Number(plan.allocation?.compare_at_price ?? variant.compare_at_price ?? 0);
      root.querySelectorAll('[data-ahc-unit-price]').forEach((node) => { node.textContent = formatMoney(variantPacks ? unitPrice / activeQuantity : unitPrice, currency); });
      root.querySelectorAll('[data-ahc-selection-price]').forEach((node) => { node.textContent = formatMoney(unitPrice * cartQuantity(), currency); });
      root.querySelectorAll('[data-ahc-compare-price]').forEach((node) => {
        node.textContent = comparePrice > unitPrice ? formatMoney(comparePrice * cartQuantity(), currency) : '';
        node.hidden = comparePrice <= unitPrice;
      });
      const percent = comparePrice > unitPrice
        ? Math.round((1 - unitPrice / comparePrice) * 100)
        : 0;
      root.querySelectorAll('[data-ahc-sale-percent]').forEach((node) => {
        node.textContent = percent > 0 ? `${percent}% OFF` : '';
        node.hidden = percent <= 0;
      });
      root.querySelectorAll('[data-ahc-total]').forEach((node) => { node.textContent = formatMoney(unitPrice * cartQuantity(), currency); });
      root.querySelectorAll('[data-ahc-pack-price]').forEach((node) => {
        const quantity = Number(node.closest('[data-ahc-pack]')?.dataset.qty || 1);
        const target = variantPacks ? packVariant(quantity) : null;
        const allocation = plan.id && target?.selling_plan_allocations.find((item) => item.plan_name === plan.allocation?.plan_name);
        node.textContent = formatMoney(variantPacks ? (allocation?.price ?? target?.price ?? 0) : unitPrice * quantity, currency);
      });
      packButtons.forEach((pack) => {
        const quantity = Number(pack.dataset.qty || 1);
        pack.disabled = !canBuyQuantity(variantPacks ? packVariant(quantity) : variant, variantPacks ? 1 : quantity);
        pack.setAttribute('aria-disabled', pack.disabled ? 'true' : 'false');
      });
      if (!canBuyQuantity(variant, cartQuantity())) {
        const fallback = packButtons.find((pack) => !pack.disabled);
        if (fallback) {
          activeQuantity = Number(fallback.dataset.qty || 1);
          packButtons.forEach((pack) => pack.setAttribute('aria-pressed', pack === fallback ? 'true' : 'false'));
          sync();
          return;
        }
      }
      const available = canBuyQuantity(variant, cartQuantity());
      root.querySelectorAll('[data-ahc-atc]').forEach((button) => {
        button.disabled = !available || !plan.valid || root.dataset.ahcSubmitting === 'true';
        const label = button.querySelector('[data-ahc-atc-label]');
        if (label) label.textContent = available ? (root.dataset.atcLabel || 'ADD TO CART') : (root.dataset.soldOutLabel || 'SOLD OUT');
      });
      root.querySelectorAll('[data-ahc-availability]').forEach((node) => {
        if (!variant.available) node.textContent = root.dataset.soldOutLabel || 'Sold out';
        else if (variant.inventory_management === 'shopify' && variant.inventory_policy !== 'continue' && Number(variant.inventory_quantity) <= 8) {
          node.textContent = `${Math.max(0, Number(variant.inventory_quantity))} left in stock`;
        } else node.textContent = root.dataset.readyLabel || 'Ready to ship';
      });
      root.querySelectorAll('[data-ahc-sticky-pack]').forEach((node) => {
        node.textContent = `${activeQuantity} ${activeQuantity === 1 ? 'Bottle' : 'Bottles'} · ${activeQuantity * 30}-Day Supply`;
      });
      if (options.variantChanged) {
        syncGalleryToVariant(variant);
        if (root.dataset.updateUrl === 'true' && window.history && window.history.replaceState) {
          const url = new URL(window.location.href);
          url.searchParams.set('variant', variant.id);
          window.history.replaceState({}, '', url.toString());
        }
      }
    };
    root._ahcSync = sync;

    const broadcastSelection = () => {
      const variant = getVariant();
      if (!variant) return;
      document.dispatchEvent(new CustomEvent('ahc:pdp:selection', {
        detail: { source: root, variantId: variant.id, quantity: activeQuantity }
      }));
    };

    packButtons.forEach((pack) => pack.addEventListener('click', () => {
      if (pack.disabled) return;
      activeQuantity = Number(pack.dataset.qty || 1);
      packButtons.forEach((item) => item.setAttribute('aria-pressed', item === pack ? 'true' : 'false'));
      sync({ variantChanged: variantPacks });
      quantityInput?.dispatchEvent(new Event('change', { bubbles: true }));
      broadcastSelection();
    }));
    if (variantSelect) variantSelect.addEventListener('change', () => {
      if (variantPacks) {
        activeQuantity = Number(variants.find((variant) => String(variant.id) === variantSelect.value)?.title.split(' ')[0] || 1);
        packButtons.forEach((pack) => pack.setAttribute('aria-pressed', Number(pack.dataset.qty) === activeQuantity ? 'true' : 'false'));
      }
      sync({ variantChanged: true });
      broadcastSelection();
    });
    const syncExternalSelection = (event) => {
      if (!event.detail || event.detail.source === root) return;
      if (!variants.some((variant) => String(variant.id) === String(event.detail.variantId))) return;
      const matchingOption = variantSelect && [...variantSelect.options].find((option) => String(option.value) === String(event.detail.variantId) && !option.disabled);
      if (matchingOption) variantSelect.value = matchingOption.value;
      activeQuantity = Math.max(1, Number(event.detail.quantity || 1));
      packButtons.forEach((pack) => pack.setAttribute('aria-pressed', Number(pack.dataset.qty || 1) === activeQuantity ? 'true' : 'false'));
      sync({ variantChanged: Boolean(matchingOption) });
    };
    root._ahcSelectionHandler = syncExternalSelection;
    document.addEventListener('ahc:pdp:selection', syncExternalSelection);
    root.addEventListener('change', (event) => {
      if (event.target === nativePlan || event.target.name === 'selling_plan') sync();
      if (event.target === quantityInput) {
        readQuantity();
        sync();
      }
    });
    root.addEventListener('kaching-subscriptions:selling-plan-change', () => queueMicrotask(() => {
      readQuantity();
      sync();
    }), true);
    let planSignature = '';
    const checkPlan = () => {
      readQuantity();
      const plan = getPlanState();
      const signature = `${getVariant()?.id}:${plan.id}:${plan.appReady}:${activeQuantity}`;
      if (signature !== planSignature) { planSignature = signature; sync(); }
    };
    if (variants.some((variant) => variant.selling_plan_allocations?.length)) {
      root._ahcPlanInterval = window.setInterval(checkPlan, 250);
    }
    forms.forEach((form) => form.addEventListener('formdata', (event) => normalizePlan(event.formData)));
    forms.forEach((form) => form.addEventListener('submit', (event) => {
      readQuantity();
      sync();
      if (root.dataset.ahcSubmitting === 'true') { event.preventDefault(); return; }
      const plan = getPlanState();
      if (!plan.valid || !canBuyQuantity(getVariant(), cartQuantity())) {
        event.preventDefault();
        const error = root.querySelector('[data-ahc-form-error]');
        if (error) {
          error.textContent = !plan.valid ? (root.dataset.planError || 'Please select an available purchase option.') : (root.dataset.quantityError || 'Please select an available quantity.');
          error.classList.add('is-visible');
          error.focus();
        }
        return;
      }
      if (!window.fetch || (root.dataset.ajaxCart === 'false' && root.dataset.skipCart !== 'true')) return;
      event.preventDefault();
      submitProductForm(form, root, event.submitter);
    }));

    galleryThumbs.forEach((thumb, index) => thumb.addEventListener('click', () => {
      if (!galleryTrack) return;
      galleryTrack.scrollTo({ left: galleryTrack.clientWidth * index, behavior: reduceMotion() ? 'auto' : 'smooth' });
    }));
    if (galleryTrack && gallerySlides.length) {
      let frame = 0;
      const updateGallery = () => {
        window.cancelAnimationFrame(frame);
        frame = window.requestAnimationFrame(() => {
          const index = Math.max(0, Math.min(gallerySlides.length - 1, Math.round(galleryTrack.scrollLeft / Math.max(1, galleryTrack.clientWidth))));
          galleryThumbs.forEach((thumb, thumbIndex) => thumb.setAttribute('aria-current', thumbIndex === index ? 'true' : 'false'));
          root.querySelectorAll('[data-ahc-gallery-current]').forEach((node) => { node.textContent = String(index + 1); });
        });
      };
      galleryTrack.addEventListener('scroll', updateGallery, { passive: true });
    }

    root.querySelectorAll('[data-ahc-scroll-reviews]').forEach((button) => button.addEventListener('click', () => {
      root.querySelector('[data-ahc-reviews]')?.scrollIntoView({ behavior: reduceMotion() ? 'auto' : 'smooth', block: 'center' });
    }));
    root.querySelectorAll('[data-ahc-scroll-target]').forEach((button) => button.addEventListener('click', () => {
      const target = document.querySelector(button.dataset.ahcScrollTarget || '#ahc-offer');
      target?.scrollIntoView({ behavior: reduceMotion() ? 'auto' : 'smooth', block: 'center' });
    }));

    const stickyBar = root.querySelector('[data-ahc-buybar]');
    const primaryAtc = root.querySelector('[data-ahc-primary-atc]');
    if (stickyBar && primaryAtc && 'IntersectionObserver' in window) {
      let primaryVisible = true;
      let footerVisible = false;
      const renderSticky = () => stickyBar.setAttribute('data-visible', !primaryVisible && !footerVisible ? 'true' : 'false');
      const primaryObserver = new IntersectionObserver((entries) => {
        primaryVisible = entries[0].isIntersecting;
        renderSticky();
      }, { threshold: .1 });
      primaryObserver.observe(primaryAtc);
      root._ahcObservers = [primaryObserver];
      const footer = document.querySelector('footer');
      if (footer) {
        const footerObserver = new IntersectionObserver((entries) => {
          footerVisible = entries[0].isIntersecting;
          renderSticky();
        }, { threshold: .02 });
        footerObserver.observe(footer);
        root._ahcObservers.push(footerObserver);
      }
      stickyBar.querySelector('[data-ahc-sticky-submit]')?.addEventListener('click', () => {
        const form = forms[0];
        if (form) form.requestSubmit(form.querySelector('[type="submit"]'));
      });
    }
    if (form && packButtons.length) {
      root._ahcSubscriptionObserver = new MutationObserver((records) => {
        const widgetChanged = records.some((record) =>
          record.type === 'attributes' ? record.target.matches(subscriptionBlockSelector) :
            [...record.addedNodes].some((node) => node.nodeType === 1 &&
              (node.matches(subscriptionBlockSelector) || node.querySelector(subscriptionBlockSelector))));
        if (widgetChanged) syncSubscriptionPricing();
      });
      root._ahcSubscriptionObserver.observe(form, {
        subtree: true,
        childList: true,
        attributes: true,
        attributeFilter: ['bundle-pricing-data', 'quantity', 'variant']
      });
    }
    sync();
  };

  const initQuiz = (root) => {
    if (initialized.has(root)) return;
    initialized.add(root);
    const questions = parseJSON(root.querySelector('[data-ahc-quiz-data]'));
    if (!questions.length) return;
    const card = root.querySelector('[data-ahc-quiz-card]');
    const result = root.querySelector('[data-ahc-quiz-result]');
    const questionNode = root.querySelector('[data-ahc-question]');
    const currentNode = root.querySelector('[data-ahc-current]');
    const totalNode = root.querySelector('[data-ahc-total-questions]');
    const progressNode = root.querySelector('[data-ahc-quiz-progress]');
    const scoreNode = root.querySelector('[data-ahc-score]');
    const resultHeading = root.querySelector('[data-ahc-result-heading]');
    const resultCopy = root.querySelector('[data-ahc-result-copy]');
    let index = 0;
    let score = 0;
    if (totalNode) totalNode.textContent = questions.length;

    const render = () => {
      if (index >= questions.length) {
        card.hidden = true;
        result.classList.add('is-visible');
        if (scoreNode) scoreNode.textContent = `${score} of ${questions.length} signals selected`;
        const ratio = score / questions.length;
        const band = ratio >= .6 ? 'high' : ratio >= .25 ? 'mid' : 'low';
        if (resultHeading) resultHeading.textContent = root.dataset[`${band}Heading`] || '';
        if (resultCopy) resultCopy.textContent = root.dataset[`${band}Copy`] || '';
        resultHeading?.focus({ preventScroll: true });
        return;
      }
      questionNode.textContent = questions[index].question;
      if (currentNode) currentNode.textContent = index + 1;
      const progress = ((index + 1) / questions.length) * 100;
      if (progressNode) progressNode.style.setProperty('--quiz-progress', `${progress}%`);
    };

    root.querySelectorAll('[data-ahc-answer]').forEach((button) => button.addEventListener('click', () => {
      if (button.dataset.ahcAnswer === 'yes') score += 1;
      index += 1;
      render();
    }));
    root.querySelector('[data-ahc-retake]')?.addEventListener('click', () => {
      index = 0;
      score = 0;
      card.hidden = false;
      result.classList.remove('is-visible');
      render();
    });
    root.querySelector('[data-ahc-quiz-cta]')?.addEventListener('click', () => {
      document.querySelector(root.dataset.ctaTarget || '#ahc-offer')?.scrollIntoView({ behavior: reduceMotion() ? 'auto' : 'smooth', block: 'center' });
    });
    render();
  };

  const init = (scope = document) => {
    scope.querySelectorAll('[data-ahc-countdown]').forEach(initCountdown);
    scope.querySelectorAll('[data-ahc-product-root]').forEach(initProductRoot);
    scope.querySelectorAll('[data-ahc-quiz-root]').forEach(initQuiz);
  };
  window.AHCPdpCoreInit = init;

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init());
  else init();
  document.addEventListener('shopify:section:load', (event) => init(event.target));
  document.addEventListener('shopify:section:unload', (event) => {
    event.target.querySelectorAll('[data-ahc-countdown]').forEach((node) => window.clearInterval(node._ahcInterval));
    event.target.querySelectorAll('[data-ahc-product-root]').forEach((root) => {
      if (root._ahcSelectionHandler) document.removeEventListener('ahc:pdp:selection', root._ahcSelectionHandler);
      window.clearInterval(root._ahcPlanInterval);
      root._ahcSubscriptionObserver?.disconnect();
      (root._ahcObservers || []).forEach((observer) => observer.disconnect());
    });
  });
})();
